// @ts-nocheck
import { BuilderReportStage } from "./BuilderReportStage";
import { DemoQnaStage } from "./DemoQnaStage";
import { DemoDatabookStage } from "./DemoDatabookStage";
import { AdjustmentSummaryStage } from "./AdjustmentSummaryStage";
/** Demo workflow shell. Workbook viewing uses the shared production renderer. */
import React, { useState, useRef, useEffect } from "react";
import { X, Check, Plus } from "lucide-react";
import { BuilderStageNavigation } from "./BuilderStageNavigation";
import { DatabookUploadStage, type DatabookUploadDocument } from "./DatabookUploadStage";
import { analysisPeriodEndFiscalYear, BUILDER_PERIOD_OPTIONS } from "./analysisPeriods";
import { DemoLegalStructureStage } from "./DemoLegalStructureStage";

import { SourceCoverageStage } from "./SourceCoverageStage";
import { DemoFileReviewStage } from "./DemoFileReviewStage";
import { BuilderStageChat } from "./BuilderStageChat";


// ==================================================================
// Periods, entities, sites
// ==================================================================
const P = ["FY22", "FY23", "FY24", "FY25", "YTD26"];
const NZ = "Start Demo NZ", AU = "Start Demo Australia", EX = "Start Demo Exports", EL = "Eliminations";
const SITEW = { [NZ]: { Hamilton: 0.6, Auckland: 0.4 }, [AU]: { Melbourne: 1 }, [EX]: { "Export hub": 1 }, [EL]: { "Head office": 1 } };

function spread(base, entW, vecs) {
  const out = [];
  Object.entries(entW).forEach(([ent, ef]) => {
    Object.entries(SITEW[ent]).forEach(([site, sf]) => {
      const v = {};
      Object.entries(vecs).forEach(([k, arr]) => { v[k] = arr.map((x) => x * ef * sf); });
      out.push({ ...base, entity: ent, site, v });
    });
  });
  return out;
}
const flat = (arr) => arr.reduce((a, b) => a.concat(b), []);

// ==================================================================
// Fact stores
// ==================================================================
const GM = { "Peach Teat": 0.45, "Milk Tankers": 0.3, Troughs: 0.28, Drinkers: 0.35, Feeders: 0.32, Traps: 0.5, Letterboxes: 0.38, "Animal Health": 0.4, Fencing: 0.3, Freight: 0, Intercompany: 0 };
const SW = {
  "Peach Teat": { [NZ]: 0.62, [AU]: 0.23, [EX]: 0.15 }, "Milk Tankers": { [NZ]: 0.78, [AU]: 0.22 },
  Troughs: { [NZ]: 1 }, Drinkers: { [NZ]: 0.85, [AU]: 0.15 }, Feeders: { [NZ]: 1 },
  Traps: { [NZ]: 0.9, [EX]: 0.1 }, Letterboxes: { [NZ]: 1 }, "Animal Health": { [NZ]: 1 },
  Fencing: { [NZ]: 1 }, Freight: { [NZ]: 0.55, [AU]: 0.2, [EX]: 0.25 },
};
const SKU = (name, group, colour, connection, pack, capacity, rev, vol) => ({ name, group, colour, connection, pack, capacity, rev, vol });
const SKUS = [
  SKU("Peach Teat Pink Thread - 50", "Peach Teat", "Pink", "Thread", 50, null, [0, 0, 96, 2439, 1775], [0, 0, 24900, 612000, 454881]),
  SKU("Peach Teat Black Threaded - 50", "Peach Teat", "Black", "Thread", 50, null, [0, 0, 49, 730, 761], [0, 0, 12200, 183000, 187254]),
  SKU("Peach Teat Black Pull Thru - 50", "Peach Teat", "Black", "Pull Thru", 50, null, [0, 0, 43, 1305, 526], [0, 0, 10500, 327000, 127950]),
  SKU("Peach Teat Pink Thread Bag 10", "Peach Teat", "Pink", "Thread", 10, null, [0, 0, 0, 192, 435], [0, 0, 0, 48000, 113810]),
  SKU("Peach Teat Black Pull Thru - 10", "Peach Teat", "Black", "Pull Thru", 10, null, [0, 0, 0, 175, 433], [0, 0, 0, 44000, 112560]),
  SKU("Peach Teat Blk Pull Thru - 5", "Peach Teat", "Black", "Pull Thru", 5, null, [0, 0, 0, 98, 243], [0, 0, 0, 25000, 63000]),
  SKU("Peach Teat Pink Thread - 10DL", "Peach Teat", "Pink", "Thread", 10, null, [0, 0, 0, 60, 144], [0, 0, 0, 1900, 4650]),
  SKU("Peach Teat Pink - Export", "Peach Teat", "Pink", "Thread", null, null, [443, 619, 780, 0, 0], [137400, 179000, 220900, 0, 0]),
  SKU("Bagged Pull Thru Peach Teats, A5034 DL", "Peach Teat", null, "Pull Thru", null, null, [523, 128, 408, 0, 0], [170100, 38400, 126100, 0, 0]),
  SKU("Threaded Peach Teat Cap, PCAP", "Peach Teat", null, "Thread", null, null, [246, 168, 163, 177, 246], [75987, 43641, 40400, 44000, 55430]),
  SKU("Teat Spanner", "Peach Teat", null, null, null, null, [12, 14, 15, 16, 18], [3000, 3400, 3600, 3800, 4200]),
  SKU("Milk Tanker Feeder 60 1000 L", "Milk Tankers", null, null, null, 1000, [260, 291, 195, 133, 411], [33, 35, 24, 16, 40]),
  SKU("Milk Tanker Feeder 80 1000 L", "Milk Tankers", null, null, null, 1000, [165, 162, 117, 115, 410], [15, 14, 10, 10, 31]),
  SKU("Milk Tanker Mixer 70 1000 L", "Milk Tankers", null, null, null, 1000, [0, 0, 45, 88, 198], [0, 0, 4, 8, 17]),
  SKU("Towable 250 L Tanker", "Milk Tankers", null, null, null, 250, [120, 140, 160, 180, 341], [40, 45, 50, 56, 107]),
  SKU("80 L Trough Incl. Cover and Val", "Troughs", null, null, null, 80, [255, 265, 234, 219, 134], [2011, 1994, 1750, 1600, 939]),
  SKU("Open Trough 60 Teat", "Troughs", null, null, null, null, [160, 216, 133, 118, 167], [33, 39, 25, 22, 27]),
  SKU("Open Trough 50 Teat 500 L", "Troughs", null, null, null, 500, [286, 198, 104, 99, 101], [98, 59, 30, 28, 26]),
  SKU("Trough Float Valve", "Troughs", null, null, null, null, [30, 32, 28, 35, 38], [2400, 2500, 2200, 2700, 2900]),
  SKU("Trough Cover 80 L", "Troughs", null, null, null, 80, [22, 24, 21, 26, 28], [1100, 1150, 1000, 1250, 1350]),
  SKU("Poly Drinker 8 L", "Drinkers", null, null, null, 8, [166, 154, 90, 128, 143], [1959, 1760, 900, 1300, 1450]),
  SKU("Replacement Valve Kit", "Drinkers", null, null, null, null, [18, 20, 22, 24, 26], [800, 880, 950, 1020, 1100]),
  SKU("Calf Feeder 6 Teat", "Feeders", null, null, null, null, [45, 48, 52, 55, 58], [420, 440, 460, 480, 500]),
  SKU("Meal Feeder 40 L", "Feeders", null, null, null, 40, [60, 64, 58, 66, 70], [300, 310, 280, 320, 340]),
  SKU("Possum Trap", "Traps", null, null, null, null, [349, 294, 255, 194, 176], [7853, 6335, 5100, 3900, 3500]),
  SKU("WP Rural Letterbox Grey Black", "Letterboxes", null, null, null, null, [95, 110, 125, 140, 170], [900, 1050, 1180, 1320, 1624]),
  SKU("Drench Gun 30 ml", "Animal Health", null, null, null, null, [40, 38, 36, 42, 44], [520, 500, 470, 540, 560]),
  SKU("Fence Standoff Pack 10", "Fencing", null, null, 10, null, [25, 27, 24, 28, 30], [1500, 1600, 1400, 1700, 1800]),
  SKU("Freight Out - Export", "Freight", null, null, null, null, [331, 273, 123, 149, 276], [0, 0, 0, 0, 0]),
];
const salesAtoms = flat(SKUS.map((m, i) =>
  spread({ name: m.name, idx: i, attrs: { group: m.group, colour: m.colour, connection: m.connection, pack: m.pack, capacity: m.capacity } },
    SW[m.group] || { [NZ]: 1 },
    { rev: m.rev, vol: m.vol, gp: m.rev.map((v) => v * (GM[m.group] || 0.3)) })
));
const ICV = [180, 190, 200, 210, 220];
const icAttrs = { group: "Intercompany", colour: null, connection: null, pack: null, capacity: null };
salesAtoms.push({ name: "Intercompany sales, NZ to Australia", idx: 99, attrs: icAttrs, entity: NZ, site: "Head office", v: { rev: ICV, vol: [0, 0, 0, 0, 0], gp: [0, 0, 0, 0, 0] } });
salesAtoms.push({ name: "Intercompany sales, NZ to Australia", idx: 99, attrs: icAttrs, entity: EL, site: "Head office", v: { rev: ICV.map((x) => -x), vol: [0, 0, 0, 0, 0], gp: [0, 0, 0, 0, 0] } });

const EW = { [NZ]: 0.82, [AU]: 0.13, [EX]: 0.05 };
const PNL = [
  ["Product revenue", "Revenue", "Variable", [9800, 9200, 8900, 11200, 12600]],
  ["Freight revenue", "Revenue", "Variable", [420, 395, 360, 410, 455]],
  ["Other income", "Revenue", "Fixed", [85, 60, 75, 90, 70]],
  ["Materials", "COGS", "Variable", [-4100, -3900, -3750, -4700, -5300]],
  ["Direct labour", "COGS", "Variable", [-1350, -1290, -1260, -1520, -1680]],
  ["Freight inwards", "COGS", "Variable", [-410, -380, -365, -460, -510]],
  ["Salaries and wages", "Overheads", "Fixed", [-1650, -1700, -1760, -1850, -1950]],
  ["Rent and utilities", "Overheads", "Fixed", [-380, -390, -400, -415, -430]],
  ["Marketing", "Overheads", "Fixed", [-210, -185, -160, -240, -280]],
  ["Repairs and maintenance", "Overheads", "Fixed", [-145, -160, -150, -170, -185]],
  ["Insurance", "Overheads", "Fixed", [-95, -102, -110, -121, -130]],
  ["Professional fees", "Overheads", "Fixed", [-120, -95, -140, -180, -155]],
  ["Other opex", "Overheads", "Fixed", [-230, -215, -205, -245, -260]],
  ["Depreciation", "Below EBITDA", "Fixed", [-420, -430, -445, -460, -475]],
  ["Interest expense", "Below EBITDA", "Fixed", [-180, -165, -150, -210, -235]],
];
const pnlAtoms = flat(PNL.map(([n, st, nat, amt], i) => spread({ name: n, idx: i, attrs: { statement: st, nature: nat } }, EW, { amt })));
pnlAtoms.push({ name: "Intercompany sales", idx: 50, attrs: { statement: "Revenue", nature: "Variable" }, entity: NZ, site: "Head office", v: { amt: ICV } });
pnlAtoms.push({ name: "Intercompany sales", idx: 50, attrs: { statement: "Revenue", nature: "Variable" }, entity: EL, site: "Head office", v: { amt: ICV.map((x) => -x) } });

const BS = [
  ["Cash and equivalents", "Current assets", "Non-trade", [820, 640, 910, 750, 1020]],
  ["Trade receivables", "Current assets", "Trade WC", [1450, 1320, 1280, 1690, 1840]],
  ["Inventory", "Current assets", "Trade WC", [2100, 2260, 2050, 2480, 2710]],
  ["Prepayments", "Current assets", "Trade WC", [140, 150, 135, 160, 175]],
  ["Property plant and equipment", "Non-current assets", "Non-trade", [3200, 3350, 3280, 3410, 3520]],
  ["Intangibles", "Non-current assets", "Non-trade", [260, 240, 220, 200, 180]],
  ["Trade payables", "Current liabilities", "Trade WC", [-980, -1040, -960, -1180, -1290]],
  ["Accrued expenses", "Current liabilities", "Trade WC", [-310, -290, -320, -355, -380]],
  ["Employee provisions", "Current liabilities", "Trade WC", [-260, -275, -290, -310, -330]],
  ["Borrowings", "Non-current liabilities", "Financing", [-2400, -2250, -2100, -2350, -2500]],
  ["Related party loan", "Non-current liabilities", "Financing", [-500, -500, -500, -500, -500]],
  ["Share capital and reserves", "Equity", "Financing", [-3520, -3605, -3705, -3995, -4445]],
];
const bsAtoms = flat(BS.map(([n, sec, nwc, bal], i) => spread({ name: n, idx: i, attrs: { section: sec, nwc } }, EW, { bal })));
bsAtoms.push({ name: "Intercompany loan receivable", idx: 40, attrs: { section: "Non-current assets", nwc: "Non-trade" }, entity: NZ, site: "Head office", v: { bal: [500, 500, 500, 500, 500] } });
bsAtoms.push({ name: "Intercompany loan payable", idx: 41, attrs: { section: "Non-current liabilities", nwc: "Non-trade" }, entity: EL, site: "Head office", v: { bal: [-500, -500, -500, -500, -500] } });

const CENT = { "North Island": { [NZ]: 1 }, "South Island": { [NZ]: 1 }, Australia: { [AU]: 1 }, Europe: { [EX]: 1 } };
const CUST = [
  ["Farmlands Co-operative", "Distributor", "North Island", [1850, 1620, 1540, 2100, 2380]],
  ["PGG Wrightson", "Distributor", "North Island", [1320, 1180, 1050, 1490, 1660]],
  ["Fonterra Farm Source", "Distributor", "North Island", [980, 890, 860, 1140, 1290]],
  ["RD1 Rural Supplies", "Retail chain", "South Island", [720, 650, 610, 830, 940]],
  ["DeLaval", "White label", "Europe", [610, 585, 540, 690, 760]],
  ["Daviesway", "White label", "Australia", [430, 410, 380, 470, 520]],
  ["Blue Pacific", "White label", "Australia", [290, 270, 255, 320, 355]],
  ["Stradbally", "White label", "Europe", [240, 220, 210, 260, 290]],
  ["Patura", "White label", "Europe", [180, 170, 160, 200, 225]],
  ["Farmage", "White label", "Europe", [150, 140, 130, 170, 190]],
  ["Rural Direct", "Retail chain", "North Island", [340, 310, 290, 380, 420]],
  ["AgriCentral", "Retail chain", "South Island", [280, 255, 240, 320, 360]],
  ["Southern Farm Supplies", "Distributor", "South Island", [230, 210, 195, 265, 300]],
  ["Waikato Ag Traders", "Direct", "North Island", [190, 170, 160, 215, 240]],
  ["Canterbury Dairy Co", "Direct", "South Island", [160, 150, 140, 185, 205]],
  ["Export brokers pooled", "Direct", "Europe", [140, 130, 120, 160, 180]],
];
const custAtoms = flat(CUST.map(([n, ch, rg, rev], i) => spread({ name: n, idx: i, attrs: { channel: ch, region: rg } }, CENT[rg], { rev })));

const PUR = [
  ["NZ Polymers", "Raw materials", "Recurring", [1650, 1580, 1520, 1890, 2100]],
  ["Pacific Resins", "Raw materials", "Recurring", [920, 880, 840, 1050, 1180]],
  ["Steel and Tube", "Raw materials", "Recurring", [480, 455, 430, 540, 600]],
  ["Packright", "Packaging", "Recurring", [310, 295, 280, 350, 390]],
  ["Mainfreight", "Freight", "Recurring", [420, 400, 380, 470, 520]],
  ["NZ Post Logistics", "Freight", "Recurring", [180, 170, 160, 200, 225]],
  ["Genesis Energy", "Utilities", "Recurring", [210, 220, 230, 245, 260]],
  ["Spark Business", "IT and comms", "Recurring", [95, 100, 105, 112, 120]],
  ["Systems and licences", "IT and comms", "Ad hoc", [45, 48, 52, 58, 64]],
  ["Legal and audit panel", "Professional services", "Ad hoc", [130, 110, 150, 185, 165]],
];
const purAtoms = flat(PUR.map(([n, cat, rec, spend], i) => spread({ name: n, idx: i, attrs: { category: cat, recurrence: rec } }, { [NZ]: 0.85, [AU]: 0.1, [EX]: 0.05 }, { spend })));

const FTEr = [
  ["Production operators", "Production", "Waged", [980, 1010, 1040, 1120, 1190], [18, 18, 19, 20, 21], { [NZ]: 1 }],
  ["Toolmakers", "Production", "Waged", [260, 270, 280, 295, 310], [3, 3, 3, 3, 3], { [NZ]: 1 }],
  ["Warehouse team", "Warehouse", "Waged", [340, 350, 365, 395, 420], [7, 7, 7, 8, 8], { [NZ]: 1 }],
  ["Drivers", "Warehouse", "Waged", [180, 185, 190, 205, 215], [3, 3, 3, 3, 3], { [NZ]: 1 }],
  ["Sales reps", "Sales", "Salaried", [420, 430, 445, 480, 510], [5, 5, 5, 5, 5], { [NZ]: 0.8, [AU]: 0.2 }],
  ["Customer services", "Sales", "Salaried", [190, 195, 200, 215, 225], [4, 4, 4, 4, 4], { [NZ]: 1 }],
  ["Finance and admin", "Admin", "Salaried", [310, 320, 330, 350, 370], [4, 4, 4, 4, 4], { [NZ]: 1 }],
  ["Leadership", "Admin", "Salaried", [520, 535, 550, 580, 610], [3, 3, 3, 3, 3], { [NZ]: 1 }],
];
const fteAtoms = flat(FTEr.map(([n, dep, emp, cost, fte, ew], i) => spread({ name: n, idx: i, attrs: { department: dep, employment: emp } }, ew, { cost, fte })));

const DS = {
  sales: {
    label: "Product sales", atoms: salesAtoms, orderBy: "value", primary: "rev", base: ["rev", "vol", "gp"],
    dims: { group: { label: "Product group", prov: "stated" }, colour: { label: "Colour", prov: "inferred" }, connection: { label: "Connection", prov: "inferred" }, pack: { label: "Pack size", prov: "inferred" }, capacity: { label: "Capacity", prov: "inferred" } },
    measures: [
      { k: "rev", label: "Revenue, USD 000s", kind: "num" },
      { k: "vol", label: "Volume, units", kind: "num" },
      { k: "price", label: "Price, USD per unit", kind: "num" },
      { k: "gp", label: "Gross profit, USD 000s", kind: "num" },
      { k: "margin", label: "GP margin", kind: "pct" },
    ],
    derived: { price: (a, pi) => (a.vol[pi] > 0 ? (a.rev[pi] * 1000) / a.vol[pi] : null), margin: (a, pi) => (a.rev[pi] !== 0 ? (a.gp[pi] / a.rev[pi]) * 100 : null) },
    sources: [
      { id: "s_ye22", label: "analyse_sales_ye22", periods: [0] },
      { id: "s_ye23", label: "analyse_sales_ye2023", periods: [1] },
      { id: "s_fy24", label: "product_sales_fy24", periods: [2] },
      { id: "s_fy25", label: "product_sales_fy25", periods: [3] },
      { id: "s_ytd26", label: "product_sales_summary_apr25_nov25", periods: [4] },
    ],
  },
  pnl: {
    label: "P&L lines", atoms: pnlAtoms, orderBy: "index", primary: "amt", base: ["amt"],
    dims: { statement: { label: "Statement section", prov: "stated" }, nature: { label: "Cost nature", prov: "inferred" } },
    measures: [{ k: "amt", label: "USD 000s", kind: "num" }], derived: {},
    sources: [
      { id: "p_a22", label: "SL_annual_ye22", periods: [0] },
      { id: "p_a23", label: "SL_annual_ye23", periods: [1] },
      { id: "p_a24", label: "SL_annual_ye24", periods: [2] },
      { id: "p_ma", label: "mgmt_accounts_pl", periods: [3, 4] },
    ],
  },
  bs: {
    label: "Balance sheet", atoms: bsAtoms, orderBy: "index", primary: "bal", base: ["bal"],
    dims: { section: { label: "Statement section", prov: "stated" }, nwc: { label: "Working capital class", prov: "inferred" } },
    measures: [{ k: "bal", label: "Balance, USD 000s", kind: "num" }], derived: {},
    sources: [
      { id: "b_a22", label: "SL_annual_ye22", periods: [0] },
      { id: "b_a23", label: "SL_annual_ye23", periods: [1] },
      { id: "b_a24", label: "SL_annual_ye24", periods: [2] },
      { id: "b_ma", label: "mgmt_accounts_bs", periods: [3, 4] },
    ],
  },
  customers: {
    label: "Customers", atoms: custAtoms, orderBy: "value", primary: "rev", base: ["rev"],
    dims: { channel: { label: "Channel", prov: "stated" }, region: { label: "Region", prov: "stated" } },
    measures: [{ k: "rev", label: "Revenue, USD 000s", kind: "num" }], derived: {},
    sources: [
      { id: "c_ye23", label: "customer_sales_ye23", periods: [0, 1] },
      { id: "c_fy24", label: "customer_sales_fy24", periods: [2] },
      { id: "c_cur", label: "sales_by_customer_fy25_ytd26", periods: [3, 4] },
    ],
  },
  purchases: {
    label: "Purchases", atoms: purAtoms, orderBy: "value", primary: "spend", base: ["spend"],
    dims: { category: { label: "Spend category", prov: "stated" }, recurrence: { label: "Recurrence", prov: "inferred" } },
    measures: [{ k: "spend", label: "Spend, USD 000s", kind: "num" }], derived: {},
    sources: [
      { id: "u_hist", label: "purchases_fy22_fy24", periods: [0, 1, 2] },
      { id: "u_cur", label: "purchases_fy25_ytd26", periods: [3, 4] },
    ],
  },
  fte: {
    label: "FTE and payroll", atoms: fteAtoms, orderBy: "value", primary: "cost", base: ["cost", "fte"],
    dims: { department: { label: "Department", prov: "stated" }, employment: { label: "Employment type", prov: "inferred" } },
    measures: [
      { k: "cost", label: "Cost, USD 000s", kind: "num" },
      { k: "fte", label: "FTE", kind: "num" },
      { k: "avg", label: "Avg cost per FTE, USD 000s", kind: "num" },
    ],
    derived: { avg: (a, pi) => (a.fte[pi] > 0 ? a.cost[pi] / a.fte[pi] : null) },
    sources: [
      { id: "f_hist", label: "wages_history", periods: [0, 1, 2, 3] },
      { id: "f_cur", label: "salaries_ye26", periods: [4] },
    ],
  },
};

// Extra data-room sources so the builder reflects a real intake — monthly
// splits, entity-level cuts, drafts and prior versions available to attach
// alongside the primaries above.
Object.entries(DS).forEach(([k, d]) => {
  const base = d.label.toLowerCase().replace(/&/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  d.sources = [
    ...d.sources,
    { id: k + "_mth_fy24", label: base + "_monthly_fy24", periods: [2] },
    { id: k + "_mth_fy25", label: base + "_monthly_fy25", periods: [3] },
    { id: k + "_mth_ytd26", label: base + "_monthly_apr25_nov25", periods: [4] },
    { id: k + "_nz_fy24", label: base + "_stallion_nz_fy24", periods: [2] },
    { id: k + "_au_fy24", label: base + "_stallion_au_fy24", periods: [2] },
    { id: k + "_export_fy24", label: base + "_stallion_export_fy24", periods: [2] },
    { id: k + "_consol_fy24", label: base + "_consolidated_fy24", periods: [2] },
    { id: k + "_draft_fy25", label: base + "_draft_fy25", periods: [3] },
    { id: k + "_qtr_fy25", label: base + "_quarterly_fy25", periods: [3] },
    { id: k + "_annual_fy24", label: base + "_annual_fy24", periods: [2] },
  ];
});
const DR_MONTHS = (() => {
  const ms = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const out = [];
  for (let y = 23; y <= 25; y++) ms.forEach((m) => out.push(m + "-" + y));
  return out;
})();
const DR_EXCLUDED = [
  { id: "x1", folder: "tb", cat: "super", name: "TB_FY24_v3.xlsx", reason: "Superseded by v4_final", status: "excl" },
  { id: "x2", folder: "ma", cat: "super", name: "Mgmt accounts Mar-24 v2.xlsx", reason: "Superseded by v3", status: "excl" },
  { id: "x3", folder: "stock", cat: "super", name: "Stock listing Dec-24 draft.xlsx", reason: "Superseded by the final listing", status: "excl" },
  { id: "x4", folder: "ma", cat: "dup", name: "Mgmt accounts May-24 (conflict copy).xlsx", reason: "Duplicate of the file in use", status: "excl" },
  { id: "x5", folder: "ar", cat: "dup", name: "Sales ledger Feb-24 (1).csv", reason: "Byte-identical to the file in use", status: "excl" },
  { id: "x6", folder: "bank", cat: "dup", name: "Bank statement ANZ 00-4471 Jul-24 copy.pdf", reason: "Duplicate of the file in use", status: "excl" },
  { id: "x7", folder: "pay", cat: "dup", name: "Payroll summary Jun-24 (email copy).xlsx", reason: "Same content, different filename", status: "excl" },
  { id: "x8", folder: "ar", cat: "nodata", name: "Price list photos.zip", reason: "Images only, nothing to extract", status: "excl" },
  { id: "x9", folder: "stock", cat: "nodata", name: "Stock template (blank).xlsx", reason: "Blank template, no values", status: "excl" },
  { id: "x10", folder: "stock", cat: "nodata", name: "Site photos warehouse.zip", reason: "Images only, nothing to extract", status: "excl" },
  { id: "x11", folder: "legal", cat: "scope", name: "Brand guidelines 2023.pdf", reason: "Not financial content", status: "excl" },
  { id: "x12", folder: "bank", cat: "scope", name: "Statement scans Q1-23.pdf", reason: "Before the review window", status: "excl" },
  { id: "x13", folder: "legal", cat: "scope", name: "Org chart 2022.pptx", reason: "Reference only, no numbers", status: "excl" },
];
const DR_QUESTIONS = [
  { id: "q1", file: "TB_FY24_v4_final.xlsx", folder: "tb", short: "Which FY24 trial balance governs?", q: "Two FY24 trial balances found. v4_final agrees to the signed accounts, v3 differs by 12.4k in Other opex. v4 is in use.", a: ["Keep v4", "Use v3 instead"] },
  { id: "q2", file: "Mgmt accounts May-24.xlsx", folder: "ma", short: "TB or management accounts for May-24?", q: "May-24 management accounts differ from the trial balance by 12.4k in Other opex. The trial balance governs the databook.", a: ["Keep the TB", "Use management accounts"] },
  { id: "q3", file: "Payroll master FY24.xlsx", folder: "pay", short: "Locked file, provide access or skip?", q: "Password protected, could not be read. Payroll cost is complete from the TB, headcount detail is not.", a: ["Not needed", "I will provide access"] },
  { id: "q4", file: "Stock count Jun-24.pdf", folder: "stock", short: "Accept TB-derived closing stock?", q: "Scanned at low quality, values could not be extracted reliably. Closing stock is taken from the TB instead.", a: ["Fine as handled", "Escalate to client"] },
];
const DR_EXCL_MORE = (() => {
  const out = [];
  const push = (cat, folder, name, reason, m) => out.push({ id: "m" + out.length, cat, folder, name, reason, status: "excl", m });
  const kinds = [["ma", "Management accounts"], ["ar", "Sales ledger"], ["ap", "Purchase ledger"], ["bank", "Bank statement"], ["pay", "Payroll summary"], ["stock", "Stock listing"]];
  const count = (c) => out.filter((f) => f.cat === c).length;
  let i = 0;
  while (count("super") < 35) { const k = kinds[i % 6], m = DR_MONTHS[i % DR_MONTHS.length]; push("super", k[0], k[1] + " " + m + " v" + (1 + (i % 3)) + ".xlsx", "Superseded by a later version in use", m); i++; }
  while (count("dup") < 117) { const k = kinds[i % 6], m = DR_MONTHS[i % DR_MONTHS.length]; push("dup", k[0], k[1] + " " + m + " (copy " + (1 + (i % 4)) + ").xlsx", "Duplicate of the file in use", m); i++; }
  while (count("nodata") < 19) { const m = DR_MONTHS[i % DR_MONTHS.length]; if (i % 2) push("nodata", "stock", "Warehouse photos " + m + ".zip", "Images only, nothing to extract", m); else push("nodata", "bank", "Statement scan " + m + ".pdf", "Scan below extraction quality", m); i++; }
  while (count("scope") < 10) { const m = DR_MONTHS[i % DR_MONTHS.length]; if (i % 2) push("scope", "legal", "Insurance certificate " + m + ".pdf", "Reference document, outside scope", m); else push("scope", "legal", "Board pack " + m + ".pdf", "Not financial content", m); i++; }
  return out;
})();
const DR_ASIDE_BY = {};
[...DR_EXCLUDED, ...DR_EXCL_MORE].forEach((f) => { DR_ASIDE_BY[f.folder] = (DR_ASIDE_BY[f.folder] || 0) + 1; });
DR_EXCLUDED.forEach((f, i) => { f.ent = ["nz", "au", "ex"][i % 3]; const mm = f.name.match(/[A-Z][a-z]{2}-\d{2}|FY\d{2}|Q\d-\d{2}|\d{4}/); f.m = mm ? mm[0] : ""; });
DR_EXCL_MORE.forEach((f, i) => { f.ent = ["nz", "au", "ex", "hold"][i % 4]; });

// ==================================================================
// Engine
// ==================================================================
const getv = (a, key) => (key === "entity" ? a.entity : key === "site" ? a.site : a.attrs[key]);
const coverage = (ds, key) => {
  if (key === "sku" || key === "entity" || key === "site") return 100;
  const w = (m) => Math.abs(m.v[ds.primary].reduce((a, b) => a + Math.abs(b), 0));
  const tot = ds.atoms.reduce((s, m) => s + w(m), 0);
  const cov = ds.atoms.filter((m) => getv(m, key) != null).reduce((s, m) => s + w(m), 0);
  return Math.round((100 * cov) / (tot || 1));
};
const UIF = { fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" };

// ==================================================================
// ==================================================================
// Stage 1: Upload / data-room intake
// ==================================================================
function UploadStage({ goNext, dealContext, setDealContext, fye, setFye, scopeFrom, setScopeFrom, scopeTo, setScopeTo, includeYtd, setIncludeYtd, includeForecast, setIncludeForecast, forecastYears, setForecastYears }) {
  const [dropped, setDropped] = useState([]);
  const uploadTimers = useRef([]);
  useEffect(() => () => {
    uploadTimers.current.forEach((timer) => window.clearTimeout(timer));
  }, []);
  const handleFiles = (fs) => {
    const list = Array.from(fs || []);
    if (list.length === 0) return;
    const seen = new Set(dropped.map((file) => file.name));
    const added = list
      .filter((file) => !seen.has(file.name))
      .map((file) => ({ name: file.name, size: file.size, status: "uploading" }));
    if (added.length === 0) return;
    setDropped((prev) => {
      const currentNames = new Set(prev.map((file) => file.name));
      return [...prev, ...added.filter((file) => !currentNames.has(file.name))];
    });
    const uploadedNames = new Set(added.map((file) => file.name));
    const timer = window.setTimeout(() => {
      setDropped((previous) => previous.map((document) => (
        uploadedNames.has(document.name)
          ? { ...document, status: "ready" }
          : document
      )));
    }, 1400);
    uploadTimers.current.push(timer);
  };
  const uploadDocuments: DatabookUploadDocument[] = dropped.map((document) => ({
    key: document.name,
    name: document.name,
    size: document.size,
    status: document.status || "ready",
  }));
  const uploadsInProgress = dropped.some((document) => document.status === "uploading");

  return (
    <DatabookUploadStage
      documents={uploadDocuments}
      dealContext={dealContext || ""}
      onDealContextChange={setDealContext}
      onSaveDealContext={() => undefined}
      financialYearEnd={fye}
      onFinancialYearEndChange={setFye}
      periodOptions={BUILDER_PERIOD_OPTIONS}
      scopeFrom={scopeFrom}
      scopeTo={scopeTo}
      includeYtd={includeYtd}
      onScopeFromChange={setScopeFrom}
      onScopeToChange={setScopeTo}
      onIncludeYtdChange={setIncludeYtd}
      includeForecast={includeForecast}
      forecastYears={forecastYears}
      onIncludeForecastChange={setIncludeForecast}
      onForecastYearsChange={setForecastYears}
      onFilesSelected={handleFiles}
      onClearDocuments={() => setDropped([])}
      onRemoveDocument={(document) => {
        setDropped((previous) => previous.filter(
          (item) => item.name !== document.key,
        ));
      }}
      continueLabel="Continue →"
      continueDisabled={uploadsInProgress}
      onContinue={goNext}
    />
  );
}

// ==================================================================
// Stage 2.5: Basis of preparation — how the consolidated FS is built
// ==================================================================
/**
 * Formula components — each is a pill in the visual "recipe" shown on
 * each basis-of-preparation option. Kinds drive the pill colour so
 * users can eyeball the mix of source data at a glance.
 */
const FORMULA_KINDS = {
  cons:      { label: "Consolidation", tone: "bg-violet-500/10 text-violet-700 border-violet-500/30" },
  entity_tb: { label: "TB per entity", tone: "bg-blue-500/10 text-blue-700 border-blue-500/30" },
  entity_pl: { label: "Entity P&L",    tone: "bg-blue-500/10 text-blue-700 border-blue-500/30" },
  site_pl:   { label: "Site P&L",      tone: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
  overheads: { label: "Group overheads", tone: "bg-stone-500/10 text-stone-700 border-stone-500/30" },
  cons_adj:  { label: "Cons adjustments", tone: "bg-amber-500/10 text-amber-800 border-amber-500/30" },
  elim:      { label: "Eliminations",  tone: "bg-amber-500/10 text-amber-800 border-amber-500/30" },
  fx:        { label: "FX translation", tone: "bg-rose-500/10 text-rose-700 border-rose-500/30" },
  other:     { label: "Other",         tone: "bg-muted text-muted-foreground border-border" },
};

const BASIS_OPTIONS = [
  {
    id: "consolidated",
    title: "From consolidated financial statements",
    tag: "Recommended",
    how: "Read the consolidated file directly and drill down where entity or site detail is available. No manual elimination arithmetic needed.",
    // "Formula" — visualised as pills joined by + and =
    formula: ["cons"],
  },
  {
    id: "entity_plus_adj",
    title: "From legal entities + consolidation adjustments",
    tag: "Most flexible",
    how: "Sum the entities, then apply the eliminations schedule to reconcile down to the group number. Every group line traces back to entities.",
    formula: ["entity_tb", "cons_adj"],
  },
  {
    id: "sites",
    title: "From site-level P&Ls",
    tag: "Site-driven groups",
    how: "Roll up site-level P&Ls into a consolidated view. Legal entity is a secondary lens rather than the primary build-up.",
    formula: ["site_pl", "overheads", "cons_adj"],
  },
  {
    id: "hybrid",
    title: "Hybrid",
    tag: "Custom mix",
    how: "Mix approaches per entity or per line. Configure the specifics in the Databook builder step.",
    formula: ["site_pl", "entity_tb", "cons_adj", "fx"],
  },
];

/**
 * Visual formula for a Basis option: a row of coloured pills joined by
 * "+" and closed with "= Consolidated". Selected option is editable —
 * users can add or remove ingredients.
 */
function BasisFormula({ ingredients, selected, onAdd, onRemove }) {
  const [addOpen, setAddOpen] = useState(false);
  const [customText, setCustomText] = useState("");
  const Pill = ({ label, tone, onX }) => (
    <span className={"inline-flex items-center gap-1 rounded-md border px-2 py-1 font-semibold whitespace-nowrap " + tone} style={{ fontSize: 10.5 }}>
      {label}
      {onX && selected && (
        <button onClick={(e) => { e.stopPropagation(); onX(); }} className="opacity-60 hover:opacity-100" title="Remove">
          <X size={9} />
        </button>
      )}
    </span>
  );
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <span className="text-muted-foreground uppercase tracking-wider font-semibold mr-1.5" style={{ fontSize: 9 }}>Formula</span>
      {ingredients.map((ing, i) => {
        const k = typeof ing === "string" ? ing : ing.kind || "other";
        const meta = FORMULA_KINDS[k] || FORMULA_KINDS.other;
        const label = typeof ing === "string" ? meta.label : (ing.label || meta.label);
        return (
          <React.Fragment key={i + "-" + label}>
            {i > 0 && <span className="text-muted-foreground font-semibold" style={{ fontSize: 12 }}>+</span>}
            <Pill label={label} tone={meta.tone} onX={onRemove ? () => onRemove(i) : null} />
          </React.Fragment>
        );
      })}
      <span className="text-muted-foreground font-semibold" style={{ fontSize: 12 }}>=</span>
      <Pill label="Consolidated" tone="bg-foreground text-background border-foreground" onX={null} />
      {selected && onAdd && (
        <div className="relative">
          {addOpen ? (
            <div className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-foreground/40 bg-background px-1.5 py-0.5">
              {Object.entries(FORMULA_KINDS).filter(([k]) => k !== "cons").map(([k, meta]) => (
                <button
                  key={k}
                  onClick={(e) => { e.stopPropagation(); onAdd({ kind: k, label: meta.label }); setAddOpen(false); }}
                  className={"rounded px-1.5 py-0.5 font-semibold hover:opacity-100 opacity-90 border " + meta.tone}
                  style={{ fontSize: 9.5 }}
                >
                  {meta.label}
                </button>
              ))}
              <input
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (customText.trim()) { onAdd({ kind: "other", label: customText.trim() }); setCustomText(""); setAddOpen(false); }
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Custom…"
                className="bg-muted rounded px-1.5 py-0.5 outline-none"
                style={{ fontSize: 10, width: 90 }}
              />
              <button onClick={(e) => { e.stopPropagation(); setAddOpen(false); setCustomText(""); }}
                className="text-muted-foreground hover:text-foreground p-0.5" title="Cancel">
                <X size={11} />
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setAddOpen(true); }}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-foreground/40 text-foreground hover:bg-foreground/[0.04] px-1.5 py-1 font-semibold"
              style={{ fontSize: 10.5 }}
              title="Add another ingredient to the formula"
            >
              <Plus size={10} /> Add
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function BasisOfPreparation({ value, onChange, onBack, onNext }) {
  // Ingredients per option — start from the seed formula and let users edit.
  const [formulas, setFormulas] = useState(() => {
    const init = {};
    BASIS_OPTIONS.forEach((o) => { init[o.id] = [...o.formula]; });
    return init;
  });
  const setFor = (id, next) => setFormulas((p) => ({ ...p, [id]: next }));

  return (
    <div className="flex-1 overflow-y-auto" style={UIF}>
      <div className="builder-stage-frame pb-10 pt-6">
        <div className="mb-1 font-semibold" style={{ fontSize: 20 }}>Basis of preparation</div>
        <div className="space-y-2.5">
          {BASIS_OPTIONS.map((o) => {
            const selected = value === o.id;
            return (
              <button
                key={o.id}
                onClick={() => onChange(o.id)}
                className={"w-full text-left rounded-xl border-2 p-4 transition-colors " +
                  (selected ? "border-foreground bg-foreground/[0.03]" : "border-border bg-card hover:border-muted-foreground/40")}
              >
                <div className="flex items-start gap-3">
                  <div className={"mt-0.5 rounded-full flex items-center justify-center shrink-0 " +
                    (selected ? "bg-foreground text-background" : "bg-muted text-muted-foreground")}
                    style={{ width: 18, height: 18 }}>
                    {selected ? <Check size={11} strokeWidth={3} /> : <span style={{ fontSize: 10 }}>·</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-foreground" style={{ fontSize: 13 }}>{o.title}</span>
                      <span className={"rounded-full px-2 py-0.5 font-semibold whitespace-nowrap " +
                        (selected ? "bg-foreground text-background" : "bg-muted text-muted-foreground")}
                        style={{ fontSize: 9.5 }}>{o.tag}</span>
                    </div>

                    {/* Visual formula — the whole point of this step */}
                    <BasisFormula
                      ingredients={formulas[o.id]}
                      selected={selected}
                      onAdd={(x) => setFor(o.id, [...formulas[o.id], x])}
                      onRemove={(idx) => setFor(o.id, formulas[o.id].filter((_, i) => i !== idx))}
                    />

                    <div className="mt-3 text-foreground/80" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                      <span className="text-muted-foreground uppercase tracking-wider font-semibold mr-1.5" style={{ fontSize: 9 }}>How</span>{o.how}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button onClick={onBack} className="rounded-lg border border-border text-foreground/80 font-medium px-4 py-2 hover:bg-muted" style={{ fontSize: 12.5 }}>
            ← Back to legal entities
          </button>
          <button onClick={onNext} className="rounded-lg bg-foreground text-background font-medium px-4 py-2 hover:opacity-90" style={{ fontSize: 12.5 }}>
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================================================================
// Period coverage — mockup readiness gate. How completely each source
// type spans the review window; drives the "files aren't sufficient"
// warning and the offer to request the gaps over RFI.
// ==================================================================
const COVERAGE = [
  { type: "Trial balances", basis: "Monthly", detail: true, cells: ["full", "full", "full", "partial", "missing"] },
  { type: "Monthly management accounts", basis: "Monthly", detail: true, cells: ["partial", "full", "full", "partial", "missing"] },
  { type: "Sales ledger", basis: "Monthly", cells: ["missing", "partial", "full", "partial", "missing"] },
  { type: "Purchase ledger", basis: "Monthly", cells: ["missing", "partial", "full", "partial", "missing"] },
  { type: "Bank statements", basis: "Monthly", cells: ["full", "full", "full", "full", "partial"] },
  { type: "Payroll", basis: "Monthly", cells: ["missing", "missing", "partial", "partial", "missing"] },
  { type: "Inventory", basis: "Annual", cells: ["missing", "missing", "partial", "missing", "missing"] },
];
const DEMO_COVERAGE_CATEGORIES = [
  ["trial_balance", "Trial balances", "monthly", COVERAGE[0].cells],
  ["management_accounts", "Monthly management accounts", "monthly", COVERAGE[1].cells],
  ["ar_by_debtor", "Sales ledger", "monthly", COVERAGE[2].cells],
  ["ap_by_creditor", "Purchase ledger", "monthly", COVERAGE[3].cells],
  ["bank_statements", "Bank statements", "monthly", COVERAGE[4].cells],
  ["fte", "Payroll", "monthly", COVERAGE[5].cells],
  ["inventory_by_type", "Inventory", "annual", COVERAGE[6].cells],
].map(([source_category, label, basis, cells]) => ({
  source_category,
  label,
  file_count: 1,
  months: basis === "monthly"
    ? cells.flatMap((status, yearIndex) =>
        Array.from({ length: 12 }, (_, monthIndex) => ({
          value: `${2022 + yearIndex}-${String(monthIndex + 1).padStart(2, "0")}`,
          status: status === "missing" ? "missing" : "received",
        })))
    : [],
  years: basis === "annual"
    ? cells.map((status, yearIndex) => ({
        value: String(2022 + yearIndex),
        status: status === "missing" ? "missing" : "received",
      }))
    : [],
}));

export function SharedCoverageStage({ startFiscalYear, endFiscalYear, onBack, onProceed, onCreateRfi, onUploadRfi }) {
  return (
    <SourceCoverageStage
      startFiscalYear={startFiscalYear}
      endFiscalYear={endFiscalYear}
      coverage={{
        financial_year_end_month: 12,
        categories: DEMO_COVERAGE_CATEGORIES,
        critical_missing_months: ["2026-11", "2026-12"],
        critical_missing_pnl_months: ["2026-11", "2026-12"],
        critical_missing_balance_sheet_months: ["2026-11", "2026-12"],
        recommend_not_build: true,
        has_rfi: true,
      }}
      onBack={onBack}
      onProceed={onProceed}
      onCreateRfi={onCreateRfi}
      onUploadRfi={onUploadRfi}
      proceedLabel="Continue anyway →"
    />
  );
}

export default function DatabookBuilderEngine() {
  const [basis, setBasis] = useState("consolidated");
  const [ws, setWs] = useState(() => (
    new URLSearchParams(window.location.search).get("stage") === "qna" ? "qna" : "upload"
  ));
  const [dealContext, setDealContext] = useState("");
  const [fye, setFye] = useState("31 March");
  const [scopeFrom, setScopeFrom] = useState(P[0]);
  const [scopeTo, setScopeTo] = useState("FY25");
  const [includeYtd, setIncludeYtd] = useState(true);
  const [includeForecast, setIncludeForecast] = useState(false);
  const [forecastYears, setForecastYears] = useState("2 years");
  return (
    <div className="h-screen flex flex-col bg-card text-foreground" style={UIF}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes tieflash { from { opacity: .25 } to { opacity: 1 } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
        button, input, select, textarea { border-radius: 4px; }
        button { transition: background-color .12s ease, color .12s ease, border-color .12s ease, opacity .12s ease; }
        input, select, textarea { transition: border-color .12s ease, box-shadow .12s ease; }
        input:focus, select:focus, textarea:focus { box-shadow: 0 0 0 2px rgba(23,23,23,.10); }
        .pop { border-radius: 8px; box-shadow: 0 10px 30px rgba(23,23,23,.16); overflow: hidden; animation: popin .12s ease-out; }
        @keyframes popin { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
        button:active { transform: translateY(0.5px); }
        tr[draggable="true"] { cursor: grab; }
        tr[draggable="true"]:active { cursor: grabbing; }
        table td, table th { user-select: none; }
        ::selection { background: hsl(var(--foreground)); color: hsl(var(--background)); }
      `}</style>

      <div className="shrink-0 border-b border-border/60 bg-card">
        <div className="builder-stage-frame flex items-center gap-3 py-3">
          <span className="font-bold tracking-tight" style={{ fontSize: 17 }}>dilli</span>
          <div className="min-w-0 flex-1 overflow-x-auto">
            <BuilderStageNavigation activeStage={ws} questionsBadge={DR_QUESTIONS.length} onStageSelect={setWs} fullWidth />
          </div>
        </div>
      </div>
      {ws === "upload" && (
        <UploadStage
          dealContext={dealContext}
          setDealContext={setDealContext}
          fye={fye}
          setFye={setFye}
          scopeFrom={scopeFrom}
          setScopeFrom={setScopeFrom}
          scopeTo={scopeTo}
          setScopeTo={setScopeTo}
          includeYtd={includeYtd}
          setIncludeYtd={setIncludeYtd}
          includeForecast={includeForecast}
          setIncludeForecast={setIncludeForecast}
          forecastYears={forecastYears}
          setForecastYears={setForecastYears}
          goNext={() => { setWs("coverage"); }}
        />
      )}

      {ws === "files" && (
        <DemoFileReviewStage
          onContinue={() => setWs("qna")}
        />
      )}

      {ws === "ents" && (
        <DemoLegalStructureStage
          onContinue={() => {
            setWs("basis");
          }}
        />
      )}

      {ws === "basis" && (
        <BasisOfPreparation
          value={basis}
          onChange={setBasis}
          onBack={() => setWs("ents")}
          onNext={() => setWs("book")}
        />
      )}

      {ws === "report" && <BuilderReportStage />}

      <div className={ws === "qna" ? "flex min-h-0 flex-1" : "hidden"}><DemoQnaStage onBack={() => setWs("book")} /></div>
      <AdjustmentSummaryStage active={ws === "adjustments"} onContinue={() => setWs("adjusted-databook")} />

      {ws === "coverage" && (
        <SharedCoverageStage
          startFiscalYear={Number(scopeFrom.replace("FY", "20"))}
          endFiscalYear={analysisPeriodEndFiscalYear(Number(scopeTo.replace("FY", "20")), includeYtd)}
          onBack={() => setWs("upload")}
          onProceed={() => setWs("ents")}
          onCreateRfi={() => window.location.assign("/revised-rfi")}
          onUploadRfi={() => window.location.assign("/rfi-tracker?mode=upload")}
        />
      )}

      {["book", "adjusted-databook"].includes(ws) && (
        <DemoDatabookStage onBack={() => setWs(ws === "adjusted-databook" ? "adjustments" : "basis")} />
      )}
      <BuilderStageChat title="Ask Dilli" intro="Ask about this stage, the files, or what to do next." placeholder="Ask Dilli anything" />
    </div>
  );
}

const Pill = ({ st, inverse }) => {
  if (!st || st === "auto") return null;
  const label = st === "review" ? "Review" : st === "custom" ? "Custom" : st === "draft" ? "Draft" : "Auto";
  const cls = inverse ? "border-white text-background"
    : st === "review" ? "bg-foreground text-background border-foreground"
    : st === "custom" ? "border-foreground text-foreground font-semibold"
    : st === "draft" ? "border-foreground border-dashed text-foreground"
    : "border-border text-muted-foreground/70";
  return <span className={"border rounded-sm px-1 " + cls} style={{ fontSize: 9, lineHeight: "13px" }}>{label}</span>;
};
